import { useEffect, useRef, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";
import { SwitchToggle } from "@/components/toggles";
import InfoCard from "@/components/toggles/InfoCard";
import {
  Loader2, Shield, CheckCircle, X, RefreshCw, Trash2, Info,
  Image as ImageIcon, Video, Download, Clock, Upload,
} from "lucide-react";

const DEFAULT_PRIVACY = {
  pixelate_face: false,
  blur_face: false,
  remove_tattoos: false,
};

function fmtCountdown(expiresAt) {
  if (!expiresAt) return null;
  const diff = new Date(expiresAt).getTime() - Date.now();
  if (diff <= 0) return null;
  const d = Math.floor(diff / 86400000);
  const h = Math.floor((diff % 86400000) / 3600000);
  if (d > 0) return `${d}d ${h}h`;
  const m = Math.floor((diff % 3600000) / 60000);
  return `${h}h ${m}m`;
}

const errMsg = (e, t, fallbackKey) => {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  return t(`order_content.${fallbackKey}`);
};

// Thumbnail-Kachel einer hochgeladenen Datei (Foto = Bild-Preview, Video = Icon)
function ContentFileTile({ orderId, file, canEdit, busy, onRemove, t }) {
  const [thumb, setThumb] = useState(null);
  useEffect(() => {
    let url = null;
    let alive = true;
    if (file.kind === "PHOTO") {
      api.get(`/orders/${orderId}/content-delivery/file/${file.file_id}/preview`, { responseType: "blob" })
        .then((res) => { if (alive) { url = URL.createObjectURL(res.data); setThumb(url); } })
        .catch(() => { /* ignore */ });
    }
    return () => { alive = false; if (url) URL.revokeObjectURL(url); };
  }, [orderId, file.file_id, file.kind]);

  return (
    <div data-testid={`content-file-${file.file_id}`} title={file.original_filename}
      className="relative aspect-square rounded-xl overflow-hidden border border-[#E5E5E5] bg-[#FAFAFA] group">
      {file.kind === "PHOTO" ? (
        thumb ? <img src={thumb} alt="" className="w-full h-full object-cover" />
          : <div className="w-full h-full flex items-center justify-center"><Loader2 size={16} className="animate-spin text-[#CCC]" /></div>
      ) : (
        <div className="w-full h-full flex flex-col items-center justify-center gap-1 text-blue-600">
          <Video size={22} />
          <span className="text-[9px] text-[#999] px-1 truncate max-w-full">{file.original_filename}</span>
        </div>
      )}
      <span className="absolute top-1 left-1 text-[8px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-black/60 text-white">
        {file.kind === "PHOTO" ? "Foto" : "Video"}
      </span>
      {canEdit && (
        <button data-testid={`content-file-remove-${file.file_id}`} onClick={() => onRemove(file.file_id)} disabled={busy}
          aria-label={t("common.close")}
          className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/60 text-white flex items-center justify-center hover:bg-red-600 disabled:opacity-40 transition-colors">
          <Trash2 size={12} />
        </button>
      )}
    </div>
  );
}

// ─── SELLER: Upload + Bereitstellen ───────────────────────────────
export function OrderContentDeliverySeller({ orderId }) {
  const { t } = useTranslation();
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [confirmState, setConfirmState] = useState(null); // {kind,fileId,message,confirmLabel,confirmTestId,tone}

  const [staging, setStaging] = useState(null);
  const [preview, setPreview] = useState(null);
  const [privacy, setPrivacy] = useState(DEFAULT_PRIVACY);
  const [uploading, setUploading] = useState(false);
  const [anonymizing, setAnonymizing] = useState(false);
  const [anonElapsed, setAnonElapsed] = useState(0);
  const anonAbortRef = useRef(false);
  const [confirming, setConfirming] = useState(false);

  const photoRef = useRef(null);
  const videoRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/orders/${orderId}/content-delivery`);
      setInfo(data);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [orderId]);

  useEffect(() => { load(); }, [load]);

  const refreshPreview = async (stagingId, variant) => {
    try {
      const res = await api.get(`/orders/${orderId}/content-delivery/photo/staging/${stagingId}/preview`, {
        params: { variant, t: Date.now() }, responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      setPreview((prev) => { if (prev) URL.revokeObjectURL(prev); return url; });
    } catch { /* ignore */ }
  };

  const startPhoto = async (file) => {
    if (!file) return;
    setUploading(true); setError("");
    try {
      const fd = new FormData();
      fd.append("file", file, file.name || "foto.jpg");
      const res = await api.post(`/orders/${orderId}/content-delivery/photo/staging`, fd, {
        headers: { "Content-Type": "multipart/form-data" }, timeout: 120000,
      });
      setStaging(res.data);
      setPrivacy(DEFAULT_PRIVACY);
      await refreshPreview(res.data.staging_id, "raw");
    } catch (e) {
      setError(errMsg(e, t, "err_upload_failed"));
    } finally {
      setUploading(false);
      if (photoRef.current) photoRef.current.value = "";
    }
  };

  const applyAnon = async () => {
    if (!staging) return;
    setAnonymizing(true); setError(""); setAnonElapsed(0);
    anonAbortRef.current = false;
    const startTs = Date.now();
    const ticker = setInterval(() => setAnonElapsed(Math.floor((Date.now() - startTs) / 1000)), 1000);
    try {
      // iter173t · Asynchron: Job starten → Status pollen (kein Proxy-Timeout).
      await api.post(`/orders/${orderId}/content-delivery/photo/staging/${staging.staging_id}/anonymize`, privacy, { timeout: 60000 });
      const statusUrl = `/orders/${orderId}/content-delivery/photo/staging/${staging.staging_id}/anonymize-status`;
      const deadline = Date.now() + 240000;
      let result = null;
      while (Date.now() < deadline) {
        if (anonAbortRef.current) return;
        await new Promise((r) => setTimeout(r, 2500));
        if (anonAbortRef.current) return;
        let st;
        try { st = (await api.get(statusUrl, { timeout: 30000 })).data; } catch { continue; }
        if (st.state === "DONE") { result = st; break; }
        if (st.state === "FAILED") { throw new Error(st.error || "failed"); }
      }
      if (anonAbortRef.current) return;
      if (!result) throw new Error("timeout");
      setStaging((c) => ({ ...c, status: "ANONYMIZED" }));
      await refreshPreview(staging.staging_id, "anonymized");
    } catch (e) {
      setError(errMsg(e, t, "err_anon_failed"));
    } finally { clearInterval(ticker); setAnonymizing(false); }
  };

  const confirmPhoto = async () => {
    if (!staging) return;
    setConfirming(true); setError("");
    try {
      await api.post(`/orders/${orderId}/content-delivery/photo/staging/${staging.staging_id}/confirm`, {});
      if (preview) URL.revokeObjectURL(preview);
      setPreview(null); setStaging(null); setPrivacy(DEFAULT_PRIVACY);
      await load();
    } catch (e) {
      setError(errMsg(e, t, "err_confirm_failed"));
    } finally { setConfirming(false); }
  };

  const cancelPhoto = async () => {
    anonAbortRef.current = true;
    setAnonymizing(false);
    if (staging) {
      try { await api.delete(`/orders/${orderId}/content-delivery/photo/staging/${staging.staging_id}`); } catch { /* ignore */ }
    }
    if (preview) URL.revokeObjectURL(preview);
    setPreview(null); setStaging(null); setPrivacy(DEFAULT_PRIVACY);
  };

  const uploadVideo = async (file) => {
    if (!file) return;
    setBusy(true); setError("");
    try {
      const fd = new FormData();
      fd.append("file", file, file.name || "video.mp4");
      await api.post(`/orders/${orderId}/content-delivery/video`, fd, {
        headers: { "Content-Type": "multipart/form-data" }, timeout: 300000,
      });
      await load();
    } catch (e) {
      setError(errMsg(e, t, "err_video_failed"));
    } finally {
      setBusy(false);
      if (videoRef.current) videoRef.current.value = "";
    }
  };

  const removeFile = (fileId) => setConfirmState({
    kind: "remove", fileId,
    message: t("order_content.remove_confirm"),
    confirmLabel: t("order_content.remove_yes"),
    confirmTestId: "content-confirm-remove-yes", tone: "danger",
  });
  const doRemoveFile = async (fileId) => {
    setBusy(true); setError("");
    try {
      await api.delete(`/orders/${orderId}/content-delivery/file/${fileId}`);
      await load();
    } catch (e) { setError(errMsg(e, t, "err_delete_failed")); }
    finally { setBusy(false); }
  };

  const provide = () => setConfirmState({
    kind: "provide",
    message: t("order_content.provide_confirm"),
    confirmLabel: t("order_content.provide_yes"),
    confirmTestId: "content-confirm-provide-yes", tone: "primary",
  });
  const doProvide = async () => {
    setBusy(true); setError("");
    try {
      await api.post(`/orders/${orderId}/content-delivery/provide`, {});
      await load();
    } catch (e) { setError(errMsg(e, t, "err_provide_failed")); }
    finally { setBusy(false); }
  };

  const runConfirm = async () => {
    const cs = confirmState; setConfirmState(null);
    if (!cs) return;
    if (cs.kind === "remove") await doRemoveFile(cs.fileId);
    else if (cs.kind === "provide") await doProvide();
  };

  if (loading) return null;
  if (!info || !info.applicable) return null;

  const status = info.status;
  const files = info.files || [];
  const canEdit = status === "DRAFT";
  const canAdd = canEdit && files.length < (info.max_files || 10);

  const whatParts = [];
  if (info.requires?.photos) {
    whatParts.push(info.requires?.min_photos
      ? t("order_content.what_photos_min", { n: info.requires.min_photos })
      : t("order_content.what_photos"));
  }
  if (info.requires?.video) whatParts.push(t("order_content.what_video"));
  const whatStr = whatParts.join(t("order_content.what_and"));

  return (
    <div className="px-4 pb-4" data-testid="seller-content-delivery">
      <p className="text-[10px] font-bold uppercase tracking-wider text-[#666] mb-1.5">{t("order_content.seller_title")}</p>
      <div className="border border-[#E5E5E5] rounded-2xl p-3 space-y-3">
        <div className="border-l-2 border-emerald-500 bg-emerald-50 p-2 flex gap-1.5">
          <Info size={12} className="text-emerald-600 shrink-0 mt-0.5" />
          <p className="text-[10px] text-emerald-900 leading-snug">
            {t("order_content.info", { what: whatStr, max: info.max_files })}
          </p>
        </div>

        {status === "READY" && (
          <InfoCard variant="info" size="sm" testId="content-status-ready">
            {t("order_content.status_ready_seller", { date: info.expires_at ? new Date(info.expires_at).toLocaleDateString("de-DE") : "" })}
          </InfoCard>
        )}
        {status === "DOWNLOADED" && (
          <InfoCard variant="success" size="sm" testId="content-status-downloaded">
            {t("order_content.status_downloaded_seller")}
          </InfoCard>
        )}
        {status === "EXPIRED" && (
          <InfoCard variant="neutral" size="sm" testId="content-status-expired">
            {t("order_content.status_expired_seller")}
          </InfoCard>
        )}

        {files.length > 0 && (
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-2" data-testid="content-file-list">
            {files.map((f) => (
              <ContentFileTile key={f.file_id} orderId={orderId} file={f} canEdit={canEdit} busy={busy} onRemove={removeFile} t={t} />
            ))}
          </div>
        )}

        {canAdd && (
          <div className="flex gap-2">
            <button type="button" data-testid="content-upload-photo-btn" onClick={() => photoRef.current?.click()} disabled={uploading || busy}
              className="flex-1 h-10 rounded-full border border-emerald-600 text-emerald-700 text-xs font-medium flex items-center justify-center gap-1.5 disabled:opacity-40 hover:bg-emerald-50 transition-colors">
              {uploading ? <Loader2 size={13} className="animate-spin" /> : <ImageIcon size={13} />} {t("order_content.add_photo")}
            </button>
            {info.requires?.video && (
              <button type="button" data-testid="content-upload-video-btn" onClick={() => videoRef.current?.click()} disabled={busy || uploading}
                className="flex-1 h-10 rounded-full border border-blue-600 text-blue-700 text-xs font-medium flex items-center justify-center gap-1.5 disabled:opacity-40 hover:bg-blue-50 transition-colors">
                {busy ? <Loader2 size={13} className="animate-spin" /> : <Video size={13} />} {t("order_content.add_video")}
              </button>
            )}
            <input ref={photoRef} type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files?.[0] && startPhoto(e.target.files[0])} />
            <input ref={videoRef} type="file" accept="video/mp4,video/quicktime,video/webm" className="hidden" onChange={(e) => e.target.files?.[0] && uploadVideo(e.target.files[0])} />
          </div>
        )}
        {!info.requires?.photos && canAdd && (
          <p className="text-[10px] text-[#999]">{t("order_content.video_only_hint")}</p>
        )}

        {canEdit && (
          <button type="button" data-testid="content-provide-btn" onClick={provide} disabled={busy || files.length < 1}
            className="w-full h-11 rounded-full bg-[#0A0A0A] text-white text-sm font-medium flex items-center justify-center gap-1.5 disabled:opacity-40 hover:bg-[#222] transition-colors">
            <Upload size={14} /> {t("order_content.provide_btn", { n: files.length })}
          </button>
        )}

        {error && <div data-testid="content-error" className="border border-red-400 bg-red-50 text-red-700 text-xs p-2 rounded-xl">{error || t("order_content.err_generic")}</div>}
      </div>

      {/* In-App-Bestätigung (ersetzt window.confirm) */}
      {confirmState && (
        <div data-testid="content-confirm-dialog" className="fixed inset-0 z-[9999] bg-black/70 flex items-end sm:items-center justify-center p-0 sm:p-4"
          onClick={() => !busy && setConfirmState(null)}>
          <div className="bg-white w-full sm:max-w-sm rounded-t-2xl sm:rounded-2xl p-5 space-y-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start gap-2.5">
              <span className={`shrink-0 w-9 h-9 rounded-full flex items-center justify-center ${confirmState.tone === "danger" ? "bg-red-50 text-red-600" : "bg-[#F5F5F5] text-[#0A0A0A]"}`}>
                {confirmState.tone === "danger" ? <Trash2 size={16} /> : <Upload size={16} />}
              </span>
              <div>
                <p className="text-sm font-semibold text-[#0A0A0A]">{t("order_content.confirm_title")}</p>
                <p className="text-xs text-[#666] mt-1 leading-snug">{confirmState.message}</p>
              </div>
            </div>
            <div className="flex gap-2">
              <button data-testid="content-confirm-cancel" onClick={() => setConfirmState(null)} disabled={busy}
                className="flex-1 h-10 rounded-full border border-[#E5E5E5] text-[#666] text-xs font-bold hover:bg-[#F5F5F5] disabled:opacity-40 transition-colors">
                {t("order_content.cancel")}
              </button>
              <button data-testid={confirmState.confirmTestId} onClick={runConfirm} disabled={busy}
                className={`flex-1 h-10 rounded-full text-white text-xs font-bold flex items-center justify-center gap-1.5 disabled:opacity-40 transition-colors ${confirmState.tone === "danger" ? "bg-red-600 hover:bg-red-700" : "bg-[#0A0A0A] hover:bg-[#222]"}`}>
                {busy ? <Loader2 size={13} className="animate-spin" /> : (confirmState.tone === "danger" ? <Trash2 size={13} /> : <Upload size={13} />)} {confirmState.confirmLabel}
              </button>
            </div>
          </div>
        </div>
      )}

      {staging && (
        <div data-testid="content-staging-dialog" className="fixed inset-0 z-[9999] bg-black/80 flex items-end sm:items-center justify-center overflow-y-auto">
          <div className="bg-white w-full sm:max-w-lg max-h-[95vh] overflow-y-auto rounded-t-2xl sm:rounded-2xl">
            <div className="sticky top-0 bg-white flex items-center justify-between px-4 h-12 border-b border-[#E5E5E5]">
              <p className="text-sm font-semibold flex items-center gap-1.5">
                <Shield size={14} /> {staging.status === "ANONYMIZED" ? t("order_content.dlg_check") : t("order_content.dlg_choose")}
              </p>
              <button onClick={cancelPhoto} className="p-1.5 -mr-1.5" disabled={confirming} aria-label={t("order_content.cancel")}><X size={18} /></button>
            </div>
            <div className="bg-[#F5F5F5] flex items-center justify-center relative" style={{ minHeight: "40vh" }}>
              {preview ? <img src={preview} data-testid="content-staging-img" alt="" className="max-w-full object-contain" style={{ maxHeight: "40vh" }} />
                : <Loader2 className="animate-spin text-[#666]" />}
              {anonymizing && (
                <div className="absolute inset-0 flex items-center justify-center bg-white/70">
                  <div className="bg-white border border-[#E5E5E5] px-4 py-3 flex flex-col items-center gap-2 w-60">
                    <div className="flex items-center gap-2"><Loader2 size={14} className="animate-spin" /><span className="text-xs font-semibold">{t("anonymize.working")}</span></div>
                    <p className="text-[10px] text-[#666] text-center leading-snug">{t("anonymize.working_hint")}</p>
                    <p data-testid="content-anon-elapsed" className="text-[11px] text-[#0A0A0A] font-medium">{anonElapsed}s</p>
                  </div>
                </div>
              )}
            </div>
            <div className="p-4 space-y-2">
              <div className="border border-[#E5E5E5] p-3 rounded-xl">
                <p className="text-[10px] uppercase tracking-wider text-[#999] mb-2">{t("order_content.face_section")}</p>
                <label data-testid="content-face-toggle" className="flex items-center justify-between py-1 cursor-pointer">
                  <div><span className="text-sm">{t("order_content.face_toggle")}</span><p className="text-[10px] text-[#999] mt-0.5">{t("order_content.face_sub")}</p></div>
                  <SwitchToggle checked={privacy.pixelate_face} disabled={anonymizing || confirming}
                    onChange={() => setPrivacy((c) => ({ ...c, pixelate_face: !c.pixelate_face, blur_face: false }))} variant="neutral" ariaLabel={t("order_content.face_toggle")} />
                </label>
              </div>
              <div className="border border-[#E5E5E5] p-3 rounded-xl">
                <p className="text-[10px] uppercase tracking-wider text-[#999] mb-2">{t("order_content.body_section")}</p>
                {[
                  { key: "remove_tattoos", label: t("order_content.rm_tattoos"), hint: t("anonymize.remove_tattoos_hint") },
                ].map((row) => (
                  <label key={row.key} data-testid={`content-toggle-${row.key}`} className="flex items-center justify-between py-1.5 cursor-pointer">
                    <div className="pr-3">
                      <span className="text-sm">{row.label}</span>
                      {row.hint && <p className="text-[10px] text-[#999] mt-0.5 leading-snug">{row.hint}</p>}
                    </div>
                    <SwitchToggle checked={!!privacy[row.key]} disabled={anonymizing || confirming}
                      onChange={() => setPrivacy((c) => ({ ...c, [row.key]: !c[row.key] }))} variant="neutral" ariaLabel={row.label} />
                  </label>
                ))}
              </div>
              <div className="flex gap-2 pt-2">
                <button data-testid="content-anonymize-btn" onClick={applyAnon} disabled={anonymizing || confirming}
                  className="flex-1 h-12 rounded-full border border-[#0A0A0A] text-[#0A0A0A] text-sm font-medium flex items-center justify-center gap-1.5 disabled:opacity-40">
                  {anonymizing ? <><Loader2 size={13} className="animate-spin" /> {t("order_content.anonymizing")}</> : <><RefreshCw size={13} /> {t("order_content.create_preview")}</>}
                </button>
                <button data-testid="content-confirm-btn" onClick={confirmPhoto} disabled={anonymizing || confirming || staging.status !== "ANONYMIZED"}
                  className="flex-1 h-12 rounded-full bg-[#0A0A0A] text-white text-sm font-medium flex items-center justify-center gap-1.5 disabled:opacity-40">
                  {confirming ? <><Loader2 size={13} className="animate-spin" /> {t("order_content.saving")}</> : <><CheckCircle size={13} /> {t("order_content.apply")}</>}
                </button>
              </div>
              {staging.status !== "ANONYMIZED" && <p className="text-[10px] text-[#999] text-center">{t("order_content.preview_first")}</p>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── BUYER: einmaliger ZIP-Download ────────────────────────────────
export function OrderContentDownloadBuyer({ orderId }) {
  const { t } = useTranslation();
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [downloading, setDownloading] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [, setTick] = useState(0);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/orders/${orderId}/content-delivery`);
      setInfo(data);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [orderId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { const iv = setInterval(() => setTick((x) => x + 1), 60000); return () => clearInterval(iv); }, []);

  const doDownload = async () => {
    setDownloading(true); setError(""); setConfirm(false);
    try {
      const res = await api.get(`/orders/${orderId}/content-delivery/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      const cd = res.headers?.["content-disposition"] || "";
      const m = cd.match(/filename="?([^"]+)"?/);
      a.href = url; a.download = m ? m[1] : `content-${orderId}.zip`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      await load();
    } catch (e) {
      const s = e.response?.status;
      if (s === 410) setError(t("order_content.err_downloaded"));
      else if (s === 425) setError(t("order_content.err_not_ready"));
      else setError(errMsg(e, t, "err_download_failed"));
      await load();
    } finally { setDownloading(false); }
  };

  if (loading) return null;
  if (!info || !info.applicable) return null;

  const status = info.status;
  const countdown = fmtCountdown(info.expires_at);

  return (
    <div className="px-4 pb-4" data-testid="buyer-content-download">
      <p className="text-[10px] font-bold uppercase tracking-wider text-[#666] mb-1.5">{t("order_content.buyer_title")}</p>
      <div className="border border-[#E5E5E5] rounded-2xl p-3 space-y-2">
        {(status === "DRAFT" || !status) && (
          <div className="flex items-center gap-2 text-xs text-amber-700" data-testid="buyer-content-waiting">
            <Clock size={14} /> {t("order_content.buyer_waiting")}
          </div>
        )}
        {status === "READY" && info.can_download && (
          <>
            <InfoCard variant="warning" size="sm" icon={<Info size={13} className="text-amber-600" />}>
              <span>{t("order_content.once_notice")}{countdown ? t("order_content.available_for", { countdown }) : ""}</span>
            </InfoCard>
            {!confirm ? (
              <button data-testid="buyer-content-download-btn" onClick={() => setConfirm(true)} disabled={downloading}
                className="w-full h-11 rounded-full bg-[#0A0A0A] text-white text-sm font-medium flex items-center justify-center gap-1.5 disabled:opacity-40 hover:bg-[#222] transition-colors">
                <Download size={14} /> {info.file_count === 1 ? t("order_content.download_btn_one") : t("order_content.download_btn_many", { n: info.file_count })}
              </button>
            ) : (
              <div className="space-y-2" data-testid="buyer-content-confirm">
                <p className="text-xs text-[#0A0A0A] font-medium">{t("order_content.buyer_confirm_q")}</p>
                <div className="flex gap-2">
                  <button data-testid="buyer-content-confirm-yes" onClick={doDownload} disabled={downloading}
                    className="flex-1 h-10 rounded-full bg-[#0A0A0A] text-white text-xs font-bold flex items-center justify-center gap-1.5 disabled:opacity-40">
                    {downloading ? <Loader2 size={13} className="animate-spin" /> : <Download size={13} />} {t("order_content.confirm_yes")}
                  </button>
                  <button data-testid="buyer-content-confirm-no" onClick={() => setConfirm(false)} disabled={downloading}
                    className="flex-1 h-10 rounded-full border border-[#E5E5E5] text-[#666] text-xs font-bold disabled:opacity-40">{t("order_content.cancel")}</button>
                </div>
              </div>
            )}
          </>
        )}
        {status === "DOWNLOADED" && (
          <div className="flex items-center gap-2 text-xs text-emerald-700" data-testid="buyer-content-done">
            <CheckCircle size={14} /> {t("order_content.buyer_done")}
          </div>
        )}
        {status === "EXPIRED" && (
          <div className="flex items-center gap-2 text-xs text-[#999]" data-testid="buyer-content-expired">
            <Clock size={14} /> {t("order_content.buyer_expired")}
          </div>
        )}
        {error && <p className="text-[11px] text-red-600" data-testid="buyer-content-error">{error || t("order_content.err_generic")}</p>}
      </div>
    </div>
  );
}
